import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Button } from '../../shared/ui/button';
import { GroupLabel } from '../../shared/ui/group-label';
import { SettingsIcon } from '../../shared/ui/icons';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { cn } from '../../shared/lib/cn';
import { fullTime, pointTime, relativeTime, untilTime } from '../../shared/lib/format';
import {
  digestTarget,
  ownWords,
  type TDigestItem,
  type TNotificationDigest,
} from '../../entities/notification';
import type { TNotificationDigestState } from './use-notification-digest';

const COUNT_WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

const counted = (count: number): string => COUNT_WORDS[count] ?? count.toLocaleString('en-US');

// A dot on everything that still needs you, red when something is broken; on the record only when unread.
const dotClass = (item: TDigestItem, live: boolean): string | null => {
  if (live) return item.tone === 'danger' ? 'bg-danger' : 'bg-faint';
  return item.unread ? 'bg-faint' : null;
};

interface IRowProps {
  item: TDigestItem;
  live: boolean;
  now: number;
  timeZone?: string;
  onOpen: (item: TDigestItem) => void;
}

const Row = ({ item, live, now, timeZone, onOpen }: IRowProps) => {
  const navigate = useNavigate();
  const target = item.action ? digestTarget(item.action) : null;
  const dot = dotClass(item, live);
  const when = pointTime(item.at, now, timeZone);
  const due = item.dueAt ? ` · stops ${untilTime(item.dueAt, now, timeZone)}` : '';

  return (
    <li className="flex items-center gap-3 border-b border-border-row px-4 py-3 last:border-b-0">
      <span
        aria-hidden
        className={cn(
          'size-dot shrink-0 self-start rounded-full mt-label',
          dot ?? 'bg-transparent',
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn('block text-data leading-20 text-primary', item.unread && 'font-medium')}
        >
          {item.title}
        </span>
        <span className="block text-meta leading-17 text-muted">{item.body}</span>
        <span
          className="mt-1 block font-mono text-micro text-faint"
          title={`${fullTime(item.at, timeZone)} · ${relativeTime(item.at, now, timeZone)}`}
        >
          {when}
          {due}
        </span>
      </span>
      {item.action && target && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onOpen(item);
            void navigate({ to: target });
          }}
        >
          {item.action.label}
        </Button>
      )}
    </li>
  );
};

const Section = ({ title, children }: { title?: string; children: ReactNode }) => (
  <section>
    {title && <GroupLabel className="px-4 pt-3">{title}</GroupLabel>}
    <ul>{children}</ul>
  </section>
);

const calmWords = (digest: TNotificationDigest, now: number, timeZone?: string): string => {
  if (digest.record.length === 0 && digest.own.length > 0) {
    const count = digest.own.length;
    return count === 1
      ? 'The only change was yours.'
      : `All ${counted(count)} were things you did.`;
  }
  const last = digest.lastWorthTellingAt
    ? ` The last thing worth telling you was at ${pointTime(digest.lastWorthTellingAt, now, timeZone)}.`
    : ' Nothing worth telling you has happened in the last 30 days.';
  return `No open issues, nothing switched off by someone else, no secret about to expire.${last}`;
};

export const NotificationDigestHeader = ({
  state,
  onNavigate,
}: {
  state: TNotificationDigestState;
  onNavigate?: () => void;
}) => {
  const data = state.digest.data;
  const needs = data?.needsYou.length ?? 0;

  return (
    <div className="flex items-center gap-inset border-b border-border-raised px-4 py-3">
      <span className="text-body font-semibold text-primary">Notifications</span>
      {needs > 0 && <span className="font-mono text-micro text-faint">{needs} need you</span>}
      <Button
        className="ml-auto"
        size="sm"
        variant="ghost"
        disabled={!data || data.unread === 0}
        waiting={state.markAllRead.isPending}
        waitingLabel="Marking"
        onClick={() => state.markAllRead.mutate()}
      >
        Mark all read
      </Button>
      <Link
        to="/settings/notifications"
        aria-label="What reaches this list"
        title="What reaches this list"
        onClick={onNavigate}
        className="text-faint hover:text-primary"
      >
        <SettingsIcon size={14} />
      </Link>
    </div>
  );
};

export const NotificationDigestBody = ({
  state,
  timeZone,
  onNavigate,
}: {
  state: TNotificationDigestState;
  timeZone?: string;
  onNavigate?: () => void;
}) => {
  const { digest } = state;
  const now = Date.now();

  if (digest.isError) {
    return (
      <div className="p-4">
        <ErrorState error={digest.error} onRetry={() => void digest.refetch()} />
      </div>
    );
  }
  if (!digest.data) {
    return (
      <div className="p-4">
        <LoadingState rows={4} />
      </div>
    );
  }

  const data = digest.data;
  const open = (item: TDigestItem) => {
    if (item.unread) state.markRead.mutate(item.notificationIds);
    onNavigate?.();
  };
  const row = (item: TDigestItem, live: boolean) => (
    <Row key={item.key} item={item} live={live} now={now} timeZone={timeZone} onOpen={open} />
  );

  return (
    <div>
      {data.needsYou.length > 0 ? (
        <Section title="Needs you">{data.needsYou.map((item) => row(item, true))}</Section>
      ) : (
        <div className="px-6 py-6 text-center">
          <p className="text-body font-medium text-primary">Nothing needs you</p>
          <p className="mt-1 text-meta leading-17 text-muted">{calmWords(data, now, timeZone)}</p>
        </div>
      )}

      {data.record.length > 0 && (
        <Section title="For the record">{data.record.map((item) => row(item, false))}</Section>
      )}

      {state.showOwn && data.own.length > 0 && (
        <Section title="Changes you made">{data.own.map((item) => row(item, false))}</Section>
      )}

      {data.own.length > 0 && (
        <p className="border-t border-border-raised px-4 py-3 text-meta text-muted">
          {state.showOwn ? 'Your own changes are listed above. ' : `${ownWords(data.own.length)} `}
          <button
            type="button"
            className="text-primary underline underline-offset-2"
            onClick={() => state.setShowOwn(!state.showOwn)}
          >
            {state.showOwn ? 'Hide them' : 'Show them'}
          </button>
        </p>
      )}
    </div>
  );
};
